import React, { useState } from 'react';
import ShippingLabel from './ShippingLabel';
import ShippingLabelFull from './ShippingLabelFull';
import ShippingLabelEditModal from './ShippingLabelEditModal';
import { openPrintDialog } from '../../utils/printUtils';
import { PACKAGE_FEATURES, isFeatureEnabled } from '../../data/packageModels';
import { savePrintHistory, PRINT_TYPES, TEMPLATE_TYPES } from '../../utils/printHistoryUtils';
import { useAuth } from '../../context/AuthContext';

/**
 * Modal สำหรับพิมพ์ใบส่งของ
 * รองรับหลาย template และแก้ไขข้อมูลได้
 */
const ShippingLabelPrintModal = ({ 
  isOpen, 
  onClose, 
  order, 
  store,
  packageType 
}) => {
  const { currentUser } = useAuth();
  const [showEditModal, setShowEditModal] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('sticker');
  const [labelType, setLabelType] = useState('sticker'); // 'sticker' or 'full'
  const [savingHistory, setSavingHistory] = useState(false);

  if (!isOpen) return null;

  // ตรวจสอบว่า package รองรับ shipping label หรือไม่
  const canPrint = isFeatureEnabled(packageType, PACKAGE_FEATURES.SHIPPING_LABEL);
  
  if (!canPrint) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6">
          <h3 className="text-lg font-semibold mb-4">⚠️ ไม่สามารถพิมพ์ได้</h3>
          <p className="text-gray-600 mb-4">
            ฟีเจอร์นี้สำหรับร้านที่สมัครแพ็กเกจ Basic ขึ้นไป
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
          >
            ปิด
          </button>
        </div>
      </div>
    );
  }

  // ตรวจสอบว่ามี tracking number หรือไม่
  const hasTrackingNumber = order?.trackingNumber && order.trackingNumber.trim() !== '';

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleSaveEdit = (data) => {
    setEditedData(data);
    setShowEditModal(false);
  };

  const handlePrint = async () => {
    const contentId = labelType === 'sticker' 
      ? 'shipping-label-print' 
      : 'shipping-label-full-print';
    
    // บันทึก Print History (เฉพาะ Premium/Enterprise)
    const hasPrintHistory = isFeatureEnabled(packageType, PACKAGE_FEATURES.PRINT_HISTORY);
    if (hasPrintHistory && currentUser && store && order) {
      try {
        setSavingHistory(true);
        await savePrintHistory({
          storeId: store.id,
          orderId: order.id,
          printType: PRINT_TYPES.SHIPPING_LABEL,
          templateType: selectedTemplate === 'sticker' ? TEMPLATE_TYPES.STICKER : 
                       selectedTemplate === 'a4' ? TEMPLATE_TYPES.A4 : 
                       TEMPLATE_TYPES.COMPACT,
          printData: {
            orderNumber: order.orderNumber,
            buyerName: editedData?.buyerName || order.buyerName,
            buyerPhone: editedData?.buyerPhone || order.buyerPhone,
            shippingAddress: editedData?.shippingAddress || order.shippingAddress,
            trackingNumber: order.trackingNumber,
            courierCompany: order.courierCompany || order.customCourierCompany
          },
          printedBy: currentUser.uid,
          printedAt: new Date()
        });
      } catch (error) {
        console.error('Error saving print history:', error);
        // ไม่ต้องแสดง error ให้ user เพราะการพิมพ์ยังทำงานได้
      } finally {
        setSavingHistory(false);
      }
    }
    
    openPrintDialog(contentId);
  };

  const displayData = editedData || {
    buyerName: order?.buyerName || '',
    buyerPhone: order?.buyerPhone || '',
    shippingAddress: order?.shippingAddress || '',
    notes: order?.notes || ''
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
            <h3 className="text-lg font-semibold">🖨️ พิมพ์ใบส่งของ</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ✕
            </button>
          </div>

          <div className="p-6">
            {/* Template Selection */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                เลือก Template
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => {
                    setSelectedTemplate('sticker');
                    setLabelType('sticker');
                  }}
                  className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                    selectedTemplate === 'sticker'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-sm font-semibold">Sticker</div>
                  <div className="text-xs text-gray-600">10x15cm</div>
                </button>
                <button
                  onClick={() => {
                    setSelectedTemplate('a4');
                    setLabelType('full');
                  }}
                  className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                    selectedTemplate === 'a4'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-sm font-semibold">A4</div>
                  <div className="text-xs text-gray-600">21x29.7cm</div>
                </button>
                <button
                  onClick={() => {
                    setSelectedTemplate('compact');
                    setLabelType('sticker');
                  }}
                  className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                    selectedTemplate === 'compact'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-sm font-semibold">Compact</div>
                  <div className="text-xs text-gray-600">8x12cm</div>
                </button>
              </div>
            </div>

            {/* Preview */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-300">
              <div className="text-sm font-semibold text-gray-700 mb-3">👁️ Preview</div>
              <div className="bg-white rounded p-4 overflow-auto" style={{ maxHeight: '400px' }}>
                {labelType === 'sticker' ? (
                  <ShippingLabel
                    order={order}
                    store={store}
                    templateType={selectedTemplate}
                    editedData={editedData}
                  />
                ) : (
                  <ShippingLabelFull
                    order={order}
                    store={store}
                    templateType={selectedTemplate}
                    editedData={editedData}
                  />
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={handleEdit}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              >
                ✏️ แก้ไขข้อมูล
              </button>
              <button
                onClick={handlePrint}
                disabled={savingHistory}
                className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingHistory ? '⏳ กำลังบันทึก...' : '🖨️ พิมพ์'}
              </button>
            </div>

            {/* Info */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-xs text-blue-800">
                <strong>💡 คำแนะนำ:</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Sticker: เหมาะสำหรับแปะกล่อง (ยังไม่มีเลขพัสดุ)</li>
                  <li>A4: แสดงข้อมูลครบถ้วน (มีเลขพัสดุแล้ว)</li>
                  <li>Compact: เหมาะสำหรับเครื่องพิมพ์เล็ก</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <ShippingLabelEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        order={order}
        store={store}
        onSave={handleSaveEdit}
      />
    </>
  );
};

export default ShippingLabelPrintModal;

